import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { environment } from '../../../environments/environment';
import { CatalogOffering, CatalogService } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-catalog-offerings-panel',
  imports: [AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      <section class="panel">
        <div class="toolbar">
          <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreateForm()"></button>
          <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
          <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          <button pButton type="button" [label]="t('catalog.action.bulkEnableInventory')" severity="secondary" [disabled]="selectedRows().length === 0" (click)="bulkEnableInventory()"></button>
        </div>

        <div class="filter-toggle">
          <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
        </div>

        @if (showFilters()) {
          <div class="filters">
            <label>
              <span>{{ t('catalog.filter.title') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.title" (ngModelChange)="applyFilters()" />
            </label>
            <label>
              <span>{{ t('catalog.filter.code') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.code" (ngModelChange)="applyFilters()" />
            </label>
            <label>
              <span>{{ t('catalog.filter.type') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.type" (ngModelChange)="applyFilters()" />
            </label>
            <label>
              <span>{{ t('catalog.filter.status') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.status" (ngModelChange)="applyFilters()" />
            </label>
          </div>
        }

        <p-table
          [value]="rows()"
          dataKey="id"
          [paginator]="true"
          [rows]="15"
          [selection]="selectedRows()"
          (selectionChange)="onSelectionChange($event)">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 44px"><p-tableHeaderCheckbox /></th>
              <th>{{ t('columns.code') }}</th>
              <th>{{ t('catalog.offerings.media.image') }}</th>
              <th>{{ t('columns.title') }}</th>
              <th>{{ t('columns.type') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('columns.visibility') }}</th>
              <th>{{ t('columns.currency') }}</th>
              <th>{{ t('columns.basePrice') }}</th>
              <th>{{ t('catalog.offerings.inventoryEnabled') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><p-tableCheckbox [value]="row" /></td>
              <td>{{ row.code }}</td>
              <td>
                @if (resolveThumbnail(row); as thumbnail) {
                  <img class="thumb" [src]="thumbnail" [alt]="row.title" loading="lazy" />
                } @else {
                  <span class="thumb-empty">{{ t('catalog.offerings.media.noImages') }}</span>
                }
              </td>
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.title }}</button>
              </td>
              <td>{{ row.type }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.visibility }}</td>
              <td>{{ row.currency }}</td>
              <td>{{ row.basePrice }}</td>
              <td>{{ row.inventoryEnabled ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      </section>
    }
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .error { margin: 0; color: #b91c1c; }
    .thumb { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; border: 1px solid #e2e8f0; display: block; }
    .thumb-empty { font-size: 11px; color: #94a3b8; }
  `]
})
export class CatalogOfferingsPanelComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<CatalogOffering[]>([]);
  readonly rows = signal<CatalogOffering[]>([]);
  readonly selectedRows = signal<CatalogOffering[]>([]);
  readonly filters = { title: '', code: '', type: '', status: '' };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.service.listOfferings().subscribe({
      next: offerings => {
        this.sourceRows.set(offerings);
        this.applyFilters();
        this.loading.set(false);
      },
      error: () => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  applyFilters(): void {
    const title = this.filters.title.trim().toLowerCase();
    const code = this.filters.code.trim().toLowerCase();
    const type = this.filters.type.trim().toLowerCase();
    const status = this.filters.status.trim().toLowerCase();
    const filtered = this.sourceRows().filter(item =>
      (!title || item.title.toLowerCase().includes(title))
      && (!code || item.code.toLowerCase().includes(code))
      && (!type || item.type.toLowerCase().includes(type))
      && (!status || item.status.toLowerCase().includes(status)));
    this.rows.set(filtered);
    const selectedIds = new Set(this.selectedRows().map(item => item.id));
    this.selectedRows.set(filtered.filter(item => selectedIds.has(item.id)));
  }

  onSelectionChange(value: CatalogOffering[]): void {
    this.selectedRows.set(value ?? []);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/catalog/offerings', id]);
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows();
    if (selected.length === 1) {
      this.openDetail(selected[0].id);
    }
  }

  openCreateForm(): void {
    void this.router.navigate(['/catalog/offerings', 'new']);
  }

  bulkEnableInventory(): void {
    const selected = this.selectedRows();
    if (!selected.length) {
      return;
    }
    let completed = 0;
    for (const item of selected) {
      this.service.updateBehaviorFlags(item.id, {
        salesEnabled: item.salesEnabled,
        purchaseEnabled: item.purchaseEnabled,
        inventoryEnabled: true,
        reservationEnabled: item.reservationEnabled,
        rentalEnabled: item.rentalEnabled,
        assetEnabled: item.assetEnabled,
        maintenanceEnabled: item.maintenanceEnabled,
        digitalDeliveryEnabled: item.digitalDeliveryEnabled
      }).subscribe({
        next: () => {
          completed += 1;
          if (completed === selected.length) {
            this.load();
          }
        },
        error: () => this.error.set(this.t('catalog.offerings.bulkUpdateFailed'))
      });
    }
  }

  resolveThumbnail(row: CatalogOffering): string | null {
    const imageUrl = row.primaryImageUrl
      ?? row.media?.find(item => item.isPrimary)?.imageUrl
      ?? row.media?.[0]?.imageUrl;
    if (!imageUrl) {
      return null;
    }
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const normalized = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${environment.apiBaseUrl}${normalized}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
